import type { Category } from '@scayle/storefront-nuxt'
import type { SbMainNavigation } from '~/cms/storyblok/types/storyblok.d.ts'
import useRouteHelpers from '~/composables/useRouteHelpers'

/**
 * Returns custom path to CMS page (if set) instead of category path
 */
export const linkCategoryTo = (category: Category) => {
  const { path, countryLevelCustomData } = category
  const { cmsPagePath } = countryLevelCustomData
  const { toLocalePath } = useRouteHelpers()

  if (cmsPagePath) {
    return toLocalePath(cmsPagePath)
  }

  return toLocalePath(path)
}

/**
 * Checks if a category is a sale category based on either:
 * 1. Category properties containing 'sale'
 * 2. Storyblok label being 'Sale'
 */
export function isSaleCategory(category: Category | SbMainNavigation): boolean {
  if (!category) return false

  // Check if it's a Storyblok navigation item with label
  if ('label' in category) {
    return category.label?.toLowerCase() === 'sale'
  }

  // Check Scayle category properties
  if ('properties' in category && Array.isArray(category.properties)) {
    return category.properties?.some((p) => p.name === 'sale') || false
  }

  return false
}

/**
 * Check whether given category is a root category
 */
export const isRootCategory = (category: Category): boolean => {
  return category.parentId === 0
}

export const isHiddenForPLPNavigation = (category: Category) =>
  category?.shopLevelCustomData?.HideForPLPNavigation || category?.shopLevelCustomData?.HasProducts === false

export const isHiddenForMainNavigation = (category: Category) =>
  category?.shopLevelCustomData?.HideForMainNavigation || category?.shopLevelCustomData?.HasProducts === false

export const shouldShowHoverImagesImmediately = (category: Category) =>
  category?.shopLevelCustomData?.PLPShowHoverImages || false

export const getAllCategoryPaths = (categories: Category[]): string[] =>
  categories.map((category: Category) =>
    category.children
      ? ([
          category.path,
          ...getAllCategoryPaths(category.children),
        ] as string[])
      : category.path,
  ).flat()
